import {
  ersterSchritt,
  naechsterSchritt,
  schrittFinden,
  schrittPruefen,
  type Antworten,
  type Feldfehler,
  type Funnel,
  type Schritt,
} from "@fbs/funnel-schema";
import { useCallback, useMemo, useReducer } from "preact/hooks";

import type { Sitzungsstand } from "./speicher";

/**
 * Der Zustand des Funnels im Browser.
 *
 * Die Frage "welcher Schritt kommt als Nächstes" wird hier nicht beantwortet.
 * Das macht @fbs/funnel-schema, dieselbe Logik, die auch auf dem Server läuft.
 * Diese Datei hält nur fest, wo der Nutzer gerade steht, was er geantwortet
 * hat und welchen Weg er dorthin genommen ist.
 */

export type Phase = "frage" | "senden" | "fertig";

export interface FunnelZustand {
  /** Der Schritt, der gerade auf dem Bildschirm steht. */
  aktuell: string;
  /**
   * Innerhalb eines Kontaktschritts mit getrennten Bildschirmen: welches Feld.
   * Bei allen anderen Schritten immer null.
   */
  teil: number | null;
  antworten: Antworten;
  /**
   * Der tatsächlich gegangene Weg, als Stapel.
   *
   * Nicht dasselbe wie der aus den Antworten berechnete Weg: Wer eine Antwort
   * ändert, verzweigt möglicherweise anders. Der Stapel endet immer beim
   * aktuellen Schritt, alles dahinter wird beim Zurückgehen verworfen und beim
   * nächsten Weiter neu bestimmt.
   */
  besucht: string[];
  fehler: Feldfehler[];
  /** Für die Richtung der Übergangsanimation. */
  richtung: 1 | -1;
  phase: Phase;
  begonnenAm: number;
  meldung: string | null;
  referenz: string | null;
}

/**
 * Eine Antwort, die sich auf die vorherige bezieht.
 *
 * Nötig, weil mehrere Felder eines Bildschirms im selben Augenblick gefüllt
 * werden können: Genau das macht die Autovervollständigung des Browsers. Ohne
 * diesen Umweg liest jeder Aufruf denselben veralteten Stand und überschreibt
 * die Eingaben der anderen; von drei ausgefüllten Feldern überlebt eines.
 */
/**
 * Der Typ bleibt `unknown`, nicht `unknown | Aktualisierung`: `unknown`
 * schließt Funktionen ohnehin ein, und eine Vereinigung damit sagt nichts
 * zusätzlich aus. Ob eine Funktion übergeben wurde, entscheidet der Reduzierer
 * zur Laufzeit.
 */
export type Aktualisierung = (vorher: unknown) => unknown;

/**
 * Die Aktionen des Zustandsautomaten.
 *
 * Exportiert, weil der Automat für sich getestet wird: Die Navigation durch
 * einen verzweigten Funnel ist die Stelle, an der ein Fehler still bleibt und
 * teuer ist. Ein Test dafür braucht keinen Browser.
 */
export type Aktion =
  | { art: "antwort"; schritt: string; wert: unknown }
  | { art: "weiter"; funnel: Funnel }
  | { art: "zurueck"; funnel: Funnel }
  | { art: "fehler"; fehler: Feldfehler[] }
  | { art: "senden" }
  | { art: "fertig"; referenz: string | null }
  | { art: "abgebrochen"; meldung: string };

/**
 * Wie viele Bildschirme dieser Schritt hat.
 *
 * Nur Kontaktschritte teilen sich, und sie tun es entlang der Feldgruppen:
 * Die höchste vergebene Bildschirmnummer bestimmt, wie viele es sind. Stehen
 * alle Felder auf 1, ist es ein einziges Formular und die Funktion gibt null
 * zurück, wie bei jedem anderen Schritttyp.
 */
export function teileVon(schritt: Schritt): number | null {
  if (schritt.type !== "contact") return null;
  const hoechste = Math.max(...schritt.fields.map((f) => f.screen));
  return hoechste > 1 ? hoechste : null;
}

function ersterTeil(schritt: Schritt): number | null {
  return teileVon(schritt) === null ? null : 0;
}

function letzterTeil(schritt: Schritt): number | null {
  const anzahl = teileVon(schritt);
  return anzahl === null ? null : anzahl - 1;
}

export function reduzieren(zustand: FunnelZustand, aktion: Aktion): FunnelZustand {
  switch (aktion.art) {
    case "antwort": {
      // Die Funktionsform wird hier aufgelöst und nicht beim Aufrufer: Nur an
      // dieser Stelle liegt der aktuelle Stand vor, auch wenn kurz
      // hintereinander mehrere Felder gefüllt werden.
      const vorher = zustand.antworten[aktion.schritt];
      const neu =
        typeof aktion.wert === "function"
          ? (aktion.wert as Aktualisierung)(vorher)
          : aktion.wert;

      return {
        ...zustand,
        antworten: { ...zustand.antworten, [aktion.schritt]: neu },
        // Sobald der Nutzer etwas ändert, verschwindet die alte Meldung. Eine
        // stehenbleibende Fehlermeldung neben einer korrigierten Eingabe
        // verunsichert mehr, als sie hilft.
        fehler: [],
      };
    }

    case "fehler":
      return { ...zustand, fehler: aktion.fehler };

    case "weiter": {
      const schritt = schrittFinden(aktion.funnel, zustand.aktuell);
      if (!schritt) return zustand;

      // Innerhalb eines geteilten Kontaktschritts: erst das nächste Feld.
      const teile = teileVon(schritt);
      if (teile !== null && zustand.teil !== null && zustand.teil < teile - 1) {
        return { ...zustand, teil: zustand.teil + 1, richtung: 1, fehler: [] };
      }

      const naechster = naechsterSchritt(
        aktion.funnel,
        zustand.aktuell,
        zustand.antworten,
      );
      if (!naechster) return { ...zustand, phase: "senden", fehler: [] };

      return {
        ...zustand,
        aktuell: naechster.id,
        teil: ersterTeil(naechster),
        besucht: [...zustand.besucht, naechster.id],
        richtung: 1,
        fehler: [],
      };
    }

    case "zurueck": {
      if (zustand.teil !== null && zustand.teil > 0) {
        return { ...zustand, teil: zustand.teil - 1, richtung: -1, fehler: [] };
      }

      if (zustand.besucht.length < 2) return zustand;

      const stapel = zustand.besucht.slice(0, -1);
      const zielId = stapel[stapel.length - 1];
      if (zielId === undefined) return zustand;

      const ziel = schrittFinden(aktion.funnel, zielId);

      return {
        ...zustand,
        aktuell: zielId,
        // Beim Zurückgehen in einen geteilten Kontaktschritt landet der Nutzer
        // auf dessen letztem Feld, nicht auf dem ersten. Alles andere fühlt
        // sich an, als hätte man ihn zwei Schritte zurückgeworfen.
        teil: ziel ? letzterTeil(ziel) : null,
        besucht: stapel,
        richtung: -1,
        fehler: [],
      };
    }

    case "senden":
      return { ...zustand, phase: "senden", fehler: [], meldung: null };

    case "fertig":
      return { ...zustand, phase: "fertig", referenz: aktion.referenz };

    case "abgebrochen":
      // Zurück auf den letzten Bildschirm, mit einer Meldung darüber. Der
      // Nutzer hat alles ausgefüllt; ihn jetzt auf eine Fehlerseite zu
      // schicken, würde die Anfrage verlieren, die er schon getippt hat.
      return { ...zustand, phase: "frage", meldung: aktion.meldung };
  }
}

export function anfangszustand(
  funnel: Funnel,
  stand: Sitzungsstand | null,
  /**
   * Antworten, die schon feststehen, bevor der Funnel beginnt.
   *
   * Heute genau eine: die Postleitzahl von der Startseite. Ein
   * fortgesetzter Stand hat Vorrang - was jemand selbst eingetippt hat, wiegt
   * schwerer als das, was in der Adresszeile stand.
   */
  vorbelegung: Antworten = {},
): FunnelZustand {
  const start = ersterSchritt(funnel) ?? funnel.steps[0];
  const startId = start?.id ?? "";

  const fortgesetzt = stand ? schrittFinden(funnel, stand.aktuell) : undefined;

  if (stand && fortgesetzt) {
    return {
      aktuell: stand.aktuell,
      teil: ersterTeil(fortgesetzt),
      antworten: { ...vorbelegung, ...stand.antworten },
      besucht: stand.besucht.length > 0 ? stand.besucht : [stand.aktuell],
      fehler: [],
      richtung: 1,
      phase: "frage",
      begonnenAm: stand.begonnenAm,
      meldung: null,
      referenz: null,
    };
  }

  return {
    aktuell: startId,
    teil: start ? ersterTeil(start) : null,
    antworten: vorbelegung,
    besucht: startId ? [startId] : [],
    fehler: [],
    richtung: 1,
    phase: "frage",
    begonnenAm: Date.now(),
    meldung: null,
    referenz: null,
  };
}

export interface FunnelSteuerung {
  zustand: FunnelZustand;
  schritt: Schritt | undefined;
  /** Kann der Nutzer von hier aus zurück? */
  kannZurueck: boolean;
  antworten: (wert: unknown) => void;
  /**
   * Prüft den aktuellen Bildschirm und geht weiter, wenn er in Ordnung ist.
   * Gibt zurück, ob der Übergang stattgefunden hat.
   */
  weiter: () => boolean;
  zurueck: () => void;
  fertigMelden: (referenz: string | null) => void;
  abbrechen: (meldung: string) => void;
}

export function useFunnel(
  funnel: Funnel,
  stand: Sitzungsstand | null,
  vorbelegung: Antworten = {},
): FunnelSteuerung {
  const [zustand, ausloesen] = useReducer(reduzieren, undefined, () =>
    anfangszustand(funnel, stand, vorbelegung),
  );

  const schritt = useMemo(
    () => schrittFinden(funnel, zustand.aktuell),
    [funnel, zustand.aktuell],
  );

  const antworten = useCallback(
    (wert: unknown) => {
      ausloesen({ art: "antwort", schritt: zustand.aktuell, wert });
    },
    [zustand.aktuell],
  );

  const weiter = useCallback((): boolean => {
    if (!schritt) return false;

    const fehler = schrittPruefen(schritt, zustand.antworten[schritt.id]);

    /**
     * Bei einem geteilten Kontaktschritt darf nur das gerade sichtbare Feld
     * beanstandet werden. Sonst stünde beim ersten Feld schon "E-Mail fehlt",
     * obwohl danach noch zwei Bildschirme kommen.
     */
    const teil = zustand.teil;
    const sichtbar =
      schritt.type === "contact" && teil !== null
        ? fehler.filter((f) =>
            schritt.fields.some(
              (feld) => feld.screen === teil + 1 && feld.name === f.field,
            ),
          )
        : fehler;

    if (sichtbar.length > 0) {
      ausloesen({ art: "fehler", fehler: sichtbar });
      return false;
    }

    ausloesen({ art: "weiter", funnel });
    return true;
  }, [funnel, schritt, zustand.antworten, zustand.teil]);

  const zurueck = useCallback(() => {
    ausloesen({ art: "zurueck", funnel });
  }, [funnel]);

  const fertigMelden = useCallback((referenz: string | null) => {
    ausloesen({ art: "fertig", referenz });
  }, []);

  const abbrechen = useCallback((meldung: string) => {
    ausloesen({ art: "abgebrochen", meldung });
  }, []);

  const kannZurueck =
    funnel.settings.navigation.allow_back &&
    (zustand.besucht.length > 1 || (zustand.teil !== null && zustand.teil > 0));

  return {
    zustand,
    schritt,
    kannZurueck,
    antworten,
    weiter,
    zurueck,
    fertigMelden,
    abbrechen,
  };
}
